import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuthStore } from '../../stores/authStore';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { generateSlug, calculateReadingTime } from '../../utils/helpers';

const ArticleEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isContributor = user?.role === 'contributor';
  const cancelledRef = useRef(false);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rewardSettings, setRewardSettings] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    short_description: '',
    content: '',
    featured_image: '',
    category_id: '',
    status: isContributor ? 'pending' : 'draft',
    meta_title: '',
    meta_description: '',
    meta_keywords: ''
  });

  useEffect(() => {
    cancelledRef.current = false;
    loadCategories();
    if (isContributor) {
      loadRewardSettings();
    }
    if (id) loadArticle();
    return () => { cancelledRef.current = true; };
  }, [id]);

  const loadRewardSettings = async () => {
    try {
      const response = await api.get('/admin/rewards/settings');
      if (!cancelledRef.current && response.data.success) {
        setRewardSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/categories/all');
      if (!cancelledRef.current && response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const loadArticle = async () => {
    try {
      const response = await api.get(`/articles/edit/${id}`);
      if (!cancelledRef.current && response.data.success) {
        setFormData(response.data.article);
      }
    } catch (error) {
      if (!cancelledRef.current) {
        toast.error('Article non trouvé');
        navigate('/admin/articles');
      }
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTitleChange = (title) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: generateSlug(title),
      meta_title: prev.meta_title || title
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = new FormData();
    data.append('image', file);

    try {
      // Don't set Content-Type manually — let axios/browser set the boundary
      const response = await api.post('/upload', data);
      if (response.data.success) {
        updateField('featured_image', response.data.image.url);
        toast.success('Image uploadée');
      }
    } catch (error) {
      toast.error('Erreur upload');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.content) {
      toast.error('Titre et contenu requis');
      return;
    }

    if (isContributor && rewardSettings) {
      const wordCount = formData.content.replace(/<[^>]*>/g, '').trim().split(/\s+/).length;
      const minWords = parseInt(rewardSettings.min_words_count) || 300;

      if (wordCount < minWords) {
        toast.error(`Votre article doit contenir au moins ${minWords} mots (actuellement: ${wordCount})`);
        return;
      }
    }

    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        category_id: parseInt(formData.category_id) || null,
        reading_time: calculateReadingTime(formData.content),
        meta_description: formData.meta_description || formData.short_description
      };

      if (isContributor) {
        if (id) {
          await api.put(`/articles/${id}`, dataToSend);
          toast.success(formData.status === 'rejected'
            ? 'Article modifié et ressoumis pour revue !'
            : 'Soumission modifiée avec succès');
          navigate('/admin/submissions');
        } else {
          await api.post('/submissions', dataToSend);
          toast.success('Soumission envoyée avec succès !');
          navigate('/admin/submissions');
        }
      } else {
        if (id) {
          await api.put(`/articles/${id}`, dataToSend);
          toast.success('Article modifié');
        } else {
          await api.post('/articles', dataToSend);
          toast.success('Article créé');
        }
        navigate('/admin/articles');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'image', 'code-block'],
      ['clean']
    ]
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>{id ? 'Modifier' : isContributor ? 'Nouvelle soumission' : 'Nouvel'} {isContributor ? 'soumission' : 'article'} - Admin Blog</title>
      </Helmet>

      <div className="p-8 max-w-5xl">
        <h1 className="text-3xl font-bold text-white mb-2">
          {id ? 'Modifier' : isContributor ? 'Nouvelle soumission' : 'Nouvel article'}
        </h1>

        {isContributor && formData.status === 'rejected' && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 flex items-center gap-2 mb-2">
              <i className="fas fa-exclamation-triangle"></i>
              <strong>Article rejeté</strong>
            </p>
            <p className="text-gray-300 text-sm">
              Cet article a été rejeté. Vous pouvez le modifier et le resoumettre.
              Il sera automatiquement remis en attente de validation.
            </p>
          </div>
        )}

        {isContributor && formData.status !== 'rejected' && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-yellow-400 flex items-center gap-2">
              <i className="fas fa-info-circle"></i>
              <span>
                Votre article doit contenir au moins <strong>{rewardSettings?.min_words_count || 300} mots</strong> pour être validé.
                Vous gagnerez <strong>{rewardSettings?.article_reward_amount || '1.00'}€</strong> de crédit après validation.
              </span>
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-gray-300 mb-2">Titre *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="w-full px-4 py-3 bg-surface border border-gray-700 rounded-lg text-white"
              required
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Slug (URL)</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => updateField('slug', e.target.value)}
              className="w-full px-4 py-3 bg-surface border border-gray-700 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Description courte *</label>
            <textarea
              value={formData.short_description}
              onChange={(e) => updateField('short_description', e.target.value)}
              className="w-full px-4 py-3 bg-surface border border-gray-700 rounded-lg text-white"
              rows="3"
              required
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">
              Contenu *
              {isContributor && (
                <span className="ml-2 text-sm">
                  {(() => {
                    const wordCount = formData.content.replace(/<[^>]*>/g, '').trim().split(/\s+/).filter(w => w).length;
                    const minWords = parseInt(rewardSettings?.min_words_count) || 300;
                    const isValid = wordCount >= minWords;
                    return (
                      <span className={isValid ? 'text-green-400' : 'text-red-400'}>
                        ({wordCount} / {minWords} mots {isValid && '✓'})
                      </span>
                    );
                  })()}
                </span>
              )}
            </label>
            <ReactQuill
              theme="snow"
              value={formData.content}
              onChange={(content) => updateField('content', content)}
              modules={quillModules}
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Image principale</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="w-full px-4 py-3 bg-surface border border-gray-700 rounded-lg text-text"
            />
            {formData.featured_image && (
              <img src={formData.featured_image} alt="Preview" className="mt-4 h-40 rounded-lg" />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">Catégorie</label>
              <select
                value={formData.category_id}
                onChange={(e) => updateField('category_id', e.target.value)}
                className="w-full px-4 py-3 bg-surface border border-gray-700 rounded-lg text-white"
              >
                <option value="">Aucune</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {!isContributor && (
              <div>
                <label className="block text-gray-300 mb-2">Statut</label>
                <select
                  value={formData.status}
                  onChange={(e) => updateField('status', e.target.value)}
                  className="w-full px-4 py-3 bg-surface border border-gray-700 rounded-lg text-white"
                >
                  <option value="draft">Brouillon</option>
                  <option value="published">Publié</option>
                </select>
              </div>
            )}
          </div>

          <details className="bg-surface p-4 rounded-lg">
            <summary className="text-white font-medium cursor-pointer">SEO (optionnel)</summary>
            <div className="mt-4 space-y-4">
              <input
                type="text"
                placeholder="Meta titre"
                value={formData.meta_title}
                onChange={(e) => updateField('meta_title', e.target.value)}
                className="w-full px-4 py-3 bg-surface/90 border border-gray-700 rounded-lg text-white"
              />
              <textarea
                placeholder="Meta description"
                value={formData.meta_description}
                onChange={(e) => updateField('meta_description', e.target.value)}
                className="w-full px-4 py-3 bg-surface/90 border border-gray-700 rounded-lg text-white"
                rows="2"
              />
              <input
                type="text"
                placeholder="Mots-clés (séparés par des virgules)"
                value={formData.meta_keywords}
                onChange={(e) => updateField('meta_keywords', e.target.value)}
                className="w-full px-4 py-3 bg-surface/90 border border-gray-700 rounded-lg text-white"
              />
            </div>
          </details>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-3 rounded-lg font-semibold disabled:opacity-50"
            >
              {loading ? 'Enregistrement...' : id ? 'Mettre à jour' : 'Créer'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/articles')}
              className="bg-gray-700 hover:bg-gray-600 text-white px-8 py-3 rounded-lg"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default ArticleEditor;
